import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, Building2 } from "lucide-react";
import Logo from "./Logo";
import QuoteButton from "./QuoteButton";
import { CONTACT } from "../../lib/site";
import { gaEvent } from "../../lib/analytics";
import { useConsent } from "./ConsentManager";

export default function Footer() {
  const year = new Date().getFullYear();
  const { openSettings } = useConsent();
  return (
    <footer
      className="relative overflow-hidden bg-charcoal text-cream grain"
      data-testid="site-footer"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
        {/* CTA band */}
        <div className="flex flex-col items-start justify-between gap-8 border-b border-white/10 py-16 lg:flex-row lg:items-center">
          <div>
            <p className="kicker text-gold">Ready when you are</p>
            <h2 className="mt-4 max-w-xl font-serif text-4xl leading-tight md:text-5xl">
              Transform your home with premium windows &amp; doors.
            </h2>
          </div>
          <QuoteButton size="lg" testId="footer-request-quote-btn" />
        </div>

        {/* Columns */}
        <div className="grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Logo />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-cream/70">
              Premium windows &amp; doors designed for comfort, style and
              security — proudly serving homes across {CONTACT.area}.
            </p>
          </div>

          <div>
            <h3 className="kicker text-gold">Explore</h3>
            <ul className="mt-5 space-y-3 text-sm text-cream/80">
              {[
                ["Home", "/"],
                ["About Us", "/about"],
                ["Doors", "/doors"],
                ["Windows", "/windows"],
                ["Contact Us", "/contact"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="nav-link hover:text-gold transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="kicker text-gold">Get in touch</h3>
            <ul className="mt-5 space-y-4 text-sm text-cream/80">
              <li>
                <a href={CONTACT.phoneHref} onClick={() => gaEvent("phone_click", { link_location: "footer" })} className="flex items-start gap-3 hover:text-gold transition-colors">
                  <Phone className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> {CONTACT.phone}
                </a>
              </li>
              <li>
                <a href={CONTACT.emailHref} onClick={() => gaEvent("email_click", { link_location: "footer" })} className="flex items-start gap-3 hover:text-gold transition-colors">
                  <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" />{" "}
                  <span>enquiries@<wbr />eminencewindows.co.uk</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> {CONTACT.hours}
              </li>
              <li className="flex items-start gap-3">
                <Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> {CONTACT.address}
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> Serving {CONTACT.area}
              </li>
            </ul>
          </div>

          <div>
            <h3 className="kicker text-gold">Our promise</h3>
            <ul className="mt-5 space-y-3 text-sm text-cream/80">
              <li>10-year insurance-backed guarantee</li>
              <li>Save up to £200 per window</li>
              <li>Save up to £300 per door</li>
              <li>Finance available</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 py-8 text-xs text-cream/50 md:flex-row">
          <p>© {year} {CONTACT.brandFull}. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/privacy" className="hover:text-gold transition-colors" data-testid="footer-privacy-link">Privacy Notice</Link>
            <Link to="/cookies" className="hover:text-gold transition-colors" data-testid="footer-cookies-link">Cookie Policy</Link>
            <button onClick={openSettings} className="hover:text-gold transition-colors" data-testid="footer-cookie-settings">Cookie settings</button>
            <span>{CONTACT.website}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
